import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { VariantDisplayTypeValues } from "@insite/client-framework/Types/ApiModels";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
// import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductDescription, { ProductDescriptionStyles } from "@GeriMed/Overrides/Components/ProductDescription"
import themeColors, { themeFontsSize } from "@GeriMed/styles/variables";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductListVariantSwatch from "@insite/content-library/Widgets/ProductList/ProductListVariantSwatch";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showAvailability: boolean;
    showAttributes: boolean;
    showImageBadges?: boolean;
    maxImageBadges?: number;
    showTextBadges?: boolean;
    maxTextBadges?: number;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    session: state.context.session,  
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasProductContext;

export interface ProductListProductInformationStyles {
    container?: GridContainerProps;
    brandStyles?: ProductBrandStyles;
    descriptionStyles?: ProductDescriptionStyles;
    brandGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    swatchGridItem?: GridItemProps;
    imageBadgeGridItem?: GridItemProps;
    textBadgeGridItem?: GridItemProps;
    partNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    availabilityStyles?: ProductAvailabilityStyles;
    attributesGridItem?: GridItemProps;
    attributes?: ProductAttributesStyles;
    searchScoreDataGridItem?: GridItemProps;
    formularyLockoutMessage?: TypographyProps;
}

export const productInformationStyles: ProductListProductInformationStyles = {
    container: {
        gap: 10,
        css: css`
            width: 100%;

            .status-info{
                font-size: 14px;
                line-height: 22px;
                font-weight: 600;
                padding-left: 5px;
            }

            .search-formulary{
                &:before {
                    vertical-align: middle;
                    font-size: 25px;
                    line-height: 1.25em;
                    padding-right: 0;
                    content: "\\e64b";
                    font-family: 'Geriatric-Medical';
                    color: ${themeColors.brightGreen};
                    padding-left: 6px; 
                }
            }
            .search-purchase-history{
                &:before {
                    vertical-align: middle;
                    font-size: 25px;
                    line-height: 1.25em;
                    padding-right: 0;
                    content: "\\e64a";
                    font-family: 'Geriatric-Medical';
                    color: ${themeColors.heavyBlue2};
                    padding-left: 6px; 
                }
            }
            .icon-office-supplies{
                background-image: url(/userfiles/images/office-supplies.png);
                background-repeat: no-repeat;
                display: inline-block;
                vertical-align: middle;
                height: 30px;
                width: 30px;
                margin-left: 2px;
                mix-blend-mode: multiply;
            }
            .search-medical-equipment{
                background: url(/userfiles/images/medical-equipment.png) no-repeat -2px -3px;
                display: inline-block;
                vertical-align: middle;
                height: 30px;
                width: 30px;
                margin-left: 2px;
                mix-blend-mode: multiply;
            }
            .icon-text{
                line-height: 1.25em;
                font-size: .5em;
                margin: 15px 2px 0;
                vertical-align: bottom;
                text-transform: uppercase;
            }
            .formulary-text{
                color: ${themeColors.brightGreen};
            }
            .purchase-text{
                color: ${themeColors.heavyBlue2};
            }
            .med-text{
                color: ${themeColors.colorMed};
            }
            .supply-text{
                color: ${themeColors.colorOrange};
            }   
        `,
    },
    descriptionStyles: {
        productDetailLink: {
            css: css`
                width: 100%;
            `,
            typographyProps: {
                size: 20,
            },
        },
    },
    brandGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    swatchGridItem: {
        width: 12,
    },
    imageBadgeGridItem: {
        width: 12,
    },
    textBadgeGridItem: {
        width: 12,
    },
    availabilityGridItem: {
        width: 12,
    },
    attributesGridItem: {
        width: 12,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
};

const styles = productInformationStyles;

const ProductListProductInformation = ({
    productContext,
    settingsCollection,
    showBrand,
    showTitle,
    showPartNumbers,
    showAvailability,
    showAttributes,
    showImageBadges,
    maxImageBadges,
    showTextBadges,
    maxTextBadges,
    session,
}: Props) => {
    const { product } = productContext;
    if (!product) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    let variantDisplayTrait = null;
    if (product.variantTraits && product.variantTraits.length > 0) {
        const swatchedViewList = [
            VariantDisplayTypeValues.SwatchDropdown,
            VariantDisplayTypeValues.SwatchList,
            VariantDisplayTypeValues.SwatchGrid,
        ];
        const sortedVaraintTraits = product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder);
        variantDisplayTrait = sortedVaraintTraits.find(x => swatchedViewList.includes(x.displayType));
    }

const isEquipmentFormulary = product.properties["onEquipmentFormulary"];
const isPurchaseHistory = product.properties["onPurchaseHistory"];
const isFormulary = product.properties["onFormulary"];
const inventoryTypeDesc = product.properties["item_InventoryTypeDesc"];
const isMedicalEquipment = product.properties["onMedicalEquipment"];

const isFormularyLockoutPoduct = !product.canAddToCart && product.properties &&
product.properties["customerHasFormularyLockout"] === 'true' && product.properties['onFormulary'] === 'false';

    return (
        <GridContainer {...styles.container}>
           
            {showTitle && (
                <GridItem {...styles.descriptionGridItem}>
                    <ProductDescription product={productContext} extendedStyles={styles.descriptionStyles} />
                </GridItem>
            )}

            {isAuthenticated && isFormulary === 'true' &&(
                <>
                    <Typography as="span" className="search-formulary">&nbsp;</Typography>
                    <Typography as="span" className="icon-text formulary-text">{translate("On Formulary")}</Typography>
                </>
            )}
             {isAuthenticated &&isPurchaseHistory ==='true' &&(
                <>
                    <Typography as="span" className="search-purchase-history">&nbsp;</Typography>
                    <Typography as="span" className="icon-text purchase-text">{translate("On Purchase History")}</Typography>
                </>
            )}
            {isAuthenticated && isEquipmentFormulary ==='true' &&(
                <>
                <Typography as="span" className="icon-office-supplies">&nbsp;</Typography>
                <Typography as="span" className="icon-text supply-text">{translate("On Office Supplies")}</Typography>
                </>                
            )}    
             {isAuthenticated && isMedicalEquipment ==='true' &&(
                <>
                    <Typography as="span" className="search-medical-equipment">&nbsp;</Typography>
                    <Typography as="span" className="icon-text med-text">{translate("On Medical Equipment")}</Typography>
                </>                
            )}       
            {showPartNumbers && (
                <GridItem {...styles.partNumbersGridItem}>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.partNumbersStyles}
                    />
                </GridItem>
            )}
             {product.brand && showBrand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
           {isAuthenticated && inventoryTypeDesc !=null &&(
            <span className="status-info" style={{color:inventoryTypeDesc.toLowerCase().trim() === 'in stock' ? '#39b54a' : '#FFA500'}}>{inventoryTypeDesc}</span>
          )}
            {variantDisplayTrait && (
                <GridItem {...styles.swatchGridItem}>
                    <ProductListVariantSwatch product={productContext} variantTrait={variantDisplayTrait} />
                </GridItem>
            )}
            {showTextBadges && textBadges && (
                <GridItem {...styles.textBadgeGridItem}>
                    <ProductTextBadges badgeList={textBadges} maxBadges={maxTextBadges ?? 4} />{" "}
                </GridItem>
            )}
            {showImageBadges && imageBadges && (
                <GridItem {...styles.imageBadgeGridItem}>
                    <ProductImageBadges badgeList={imageBadges} maxBadges={maxImageBadges ?? 4} />
                </GridItem>
            )}
            {showAvailability && (
                <GridItem {...styles.availabilityGridItem}>
                    <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                </GridItem>
            )}
            {showAttributes && (
                <GridItem {...styles.attributesGridItem}>
                    <ProductAttributes maximumNumberAttributeTypes={3} extendedStyles={styles.attributes} />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductListProductInformation));
